import React from "react";

import Reveal from "@/components/Reveal";

import {
  Globe,
  Search,
  MessageCircle,
  ArrowRight,
  BarChart3,
  Smartphone,
} from "lucide-react";

import { WHATSAPP_URL } from "@/lib/brand";

const WEBSITE_IMG =
  "https://images.unsplash.com/photo-1498050108023-c5249f4df085";

const MARKETING_IMG =
  "https://images.unsplash.com/photo-1460925895917-afdab827c52f";

export default function Services() {
  return (
    <Reveal>
      <section
        id="services"
        data-testid="services-section"
        className="py-20 lg:py-28 bg-white border-y border-[var(--gf-border)]"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8">

          {/* Heading */}
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-[var(--gf-blue)]">
              Services
            </p>

            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08]">
              Everything your business needs to grow online.
            </h2>

            <p className="mt-5 text-lg text-[var(--gf-text-2)] leading-relaxed">
              From high-converting websites to SEO, Google Ads &
              WhatsApp automation — GrowFlow helps local businesses
              get more customers online.
            </p>
          </div>

          {/* Grid */}
          <div className="mt-14 grid md:grid-cols-12 gap-5">

            {/* Main Big Card */}
            <article className="md:col-span-7 relative rounded-3xl overflow-hidden border border-[var(--gf-border)] gf-shadow-soft min-h-[460px] group">

              <img
                src={WEBSITE_IMG}
                alt="Website Development"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-[var(--gf-primary)]/90 via-[var(--gf-primary)]/45 to-transparent" />

              <div className="relative h-full p-8 lg:p-10 flex flex-col justify-end text-white">

                <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 px-3 py-1 rounded-full w-fit text-xs font-semibold tracking-wide">

                  <Globe className="h-3.5 w-3.5" />

                  Premium Service
                </div>

                <h3 className="font-display mt-4 text-2xl sm:text-4xl font-extrabold leading-tight">
                  Modern Websites That Convert Visitors Into Customers
                </h3>

                <p className="mt-4 text-white/85 text-sm sm:text-base max-w-xl leading-relaxed">
                  Beautiful, fast-loading websites designed to increase
                  calls, WhatsApp chats, appointments and customer trust.
                </p>

                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-7 inline-flex items-center gap-2 bg-white text-[var(--gf-primary)] font-semibold h-12 px-6 rounded-full w-fit hover:-translate-y-0.5 transition-transform"
                >
                  Get My Website

                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </article>

            {/* Right Side */}
            <div className="md:col-span-5 grid gap-5">

              {/* SEO */}
              <article className="relative rounded-3xl overflow-hidden border border-[var(--gf-border)] min-h-[220px] group">

                <img
                  src={MARKETING_IMG}
                  alt="SEO and Google Ads"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/40" />

                <div className="relative p-7 max-w-md">

                  <div className="inline-flex items-center gap-2 bg-[var(--gf-mint)] text-[var(--gf-green-deep)] px-3 py-1 rounded-full text-xs font-semibold">

                    <Search className="h-3.5 w-3.5" />

                    Marketing
                  </div>

                  <h3 className="font-display mt-3 text-2xl font-extrabold">
                    SEO & Google Ads
                  </h3>

                  <p className="text-sm text-[var(--gf-text-2)] mt-3 leading-relaxed">
                    Rank higher on Google and generate more leads.
                  </p>
                </div>
              </article>

              {/* Small Cards */}
              <div className="grid sm:grid-cols-2 gap-5">

                <article className="rounded-3xl overflow-hidden border border-[var(--gf-border)] bg-[var(--gf-blue)] text-white p-6 relative">

                  <div className="absolute inset-0 gf-grid-bg opacity-30" />

                  <div className="relative">

                    <div className="h-11 w-11 rounded-2xl bg-white/15 flex items-center justify-center">

                      <MessageCircle className="h-5 w-5" />
                    </div>

                    <h3 className="font-display mt-5 text-lg font-extrabold">
                      WhatsApp Automation
                    </h3>

                    <p className="text-sm text-white/85 mt-2 leading-relaxed">
                      Convert visitors instantly with WhatsApp chat integration.
                    </p>
                  </div>
                </article>

                <article className="rounded-3xl overflow-hidden border border-[var(--gf-border)] bg-slate-50 p-6 relative">

                  <div className="h-11 w-11 rounded-2xl bg-[var(--gf-blue-soft)] flex items-center justify-center text-[var(--gf-blue)]">

                    <Smartphone className="h-5 w-5" />
                  </div>

                  <h3 className="font-display mt-5 text-lg font-extrabold">
                    Mobile Optimized
                  </h3>

                  <p className="text-sm text-[var(--gf-text-2)] mt-2 leading-relaxed">
                    Fast responsive design built perfectly for mobile users.
                  </p>
                </article>
              </div>

              {/* Analytics */}
              <article className="rounded-3xl border border-[var(--gf-border)] bg-gradient-to-r from-slate-900 to-slate-800 text-white p-7 relative overflow-hidden">

                <div className="absolute inset-0 gf-grid-bg opacity-20" />

                <div className="relative flex items-start gap-4">

                  <div className="h-12 w-12 rounded-2xl bg-white/10 flex items-center justify-center">

                    <BarChart3 className="h-6 w-6" />
                  </div>

                  <div>
                    <h3 className="font-display text-xl font-extrabold">
                      Growth Focused
                    </h3>

                    <p className="text-sm text-white/80 mt-2 leading-relaxed">
                      Every website is designed to generate leads and increase trust.
                    </p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>
    </Reveal>
  );
}
